import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import {
  ArrowsClockwise, ArrowLeft, ArrowRight, CaretDown, CaretRight,
  Check, House, Trash, Calendar, User, MapPin, Package,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminMoveRadar — Move-In / Move-Out watch list.
 * Route: /admin/move-radar
 *
 * Every event has an auto-attached punch list (pre-inspection, deposit,
 * keys, cleaning, marketing). Toggle checkboxes as you complete each
 * step; when all tasks are done the event auto-marks Complete.
 */
export default function AdminMoveRadar() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [events, setEvents] = useState([]);
  const [hidden, setHidden] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");     // all | move_in | move_out
  const [statusFilter, setStatusFilter] = useState("open"); // open | complete | dismissed | all
  const [horizon, setHorizon] = useState(60);              // 7 | 30 | 60 | 365 (All)

  const load = () => {
    setLoading(true);
    axios.get(`${BASE}/api/move-radar/events`, {
      ...auth(),
      params: { kind: kindFilter, status: statusFilter, horizon_days: horizon },
    })
      .then(r => {
        setEvents(r.data.events || []);
        setHidden(r.data.hidden || 0);
      })
      .catch(e => toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to load")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter, statusFilter, horizon]);

  const counts = useMemo(() => ({
    move_out: events.filter(e => e.kind === "move_out").length,
    move_in:  events.filter(e => e.kind === "move_in").length,
  }), [events]);

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-move-radar-page">
        <div className="flex items-center gap-3">
          <ArrowsClockwise weight="bold" size={30} className="text-indigo-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              Move Radar
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Every incoming AppFolio move-in / move-out email auto-lands here with a full punch list.
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="inline-flex border-2 border-slate-200 bg-slate-50">
            {[
              { k: "all",      label: `All (${events.length})`,        icon: null },
              { k: "move_out", label: `Move-Out (${counts.move_out})`, icon: ArrowRight },
              { k: "move_in",  label: `Move-In (${counts.move_in})`,   icon: ArrowLeft },
            ].map(f => (
              <button key={f.k} onClick={() => setKindFilter(f.k)}
                data-testid={`kind-${f.k}`}
                className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 ${
                  kindFilter === f.k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {f.icon && <f.icon size={12}/>} {f.label}
              </button>
            ))}
          </div>
          <div className="inline-flex border-2 border-slate-200 bg-slate-50 ml-auto">
            {["open", "complete", "dismissed", "all"].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                data-testid={`status-${s}`}
                className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest ${
                  statusFilter === s ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {s}
              </button>
            ))}
          </div>
          <button
            data-testid="move-radar-cleanup-btn"
            onClick={async () => {
              if (!window.confirm(
                `Purge every move event without a date, or outside ±${horizon} days of today?\n\n`+
                `New emails will already skip these rows automatically going forward — this cleans up the backlog.`
              )) return;
              try {
                const r = await axios.post(`${BASE}/api/move-radar/cleanup-stale`,
                  null, { ...auth(), params: { horizon_days: horizon } });
                toast.success(`Removed ${r.data?.deleted ?? 0} stale row${r.data?.deleted === 1 ? "" : "s"}`);
                load();
              } catch (e) {
                toast.error("Cleanup failed: " + (e?.response?.data?.detail || e.message));
              }
            }}
            className="h-9 px-3 font-black uppercase text-[10px] tracking-widest text-slate-600 hover:bg-red-50 hover:text-red-700 border-2 border-slate-200 bg-white">
            <Trash size={12} weight="bold" className="inline mr-1"/> Purge Stale
          </button>
        </div>

        {/* Horizon chips — narrow the visible date window on the fly */}
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="move-radar-horizon-row">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Horizon</span>
          {[
            { d: 7,   label: "±7d" },
            { d: 30,  label: "±30d" },
            { d: 60,  label: "±60d" },
            { d: 365, label: "All" },
          ].map(h => (
            <button key={h.d} onClick={() => setHorizon(h.d)}
              data-testid={`move-radar-horizon-${h.d}`}
              className={`h-8 px-3 rounded-full border-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                horizon === h.d
                  ? "bg-indigo-600 border-indigo-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-indigo-400"
              }`}>
              {h.label}
            </button>
          ))}
          {hidden > 0 && (
            <span data-testid="move-radar-hidden-count"
              className="ml-2 text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
              {hidden} hidden · out of window
            </span>
          )}
        </div>

        {loading && <div className="mt-8 text-center text-slate-500 font-bold">Loading…</div>}
        {!loading && events.length === 0 && (
          <div className="mt-8 p-8 border-2 border-dashed border-slate-300 bg-slate-50 text-center" data-testid="move-radar-empty">
            <ArrowsClockwise size={44} className="mx-auto mb-2 text-slate-400"/>
            <div className="font-black text-sm uppercase tracking-widest text-slate-600">
              No {statusFilter === "all" ? "" : statusFilter} move events yet
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Forward AppFolio portal requests or Moving In/Out rosters to your Office Inbox — they'll auto-populate here.
            </div>
          </div>
        )}
        {!loading && events.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="move-radar-list">
            {events.map(e => <EventCard key={e.id} event={e} onChanged={load}/>)}
          </div>
        )}
      </div>
    </AdminShell>
  );
}


function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return Math.round((d - now) / (1000 * 60 * 60 * 24));
}


function EventCard({ event, onChanged }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOut = event.kind === "move_out";
  const brand = isOut ? "rose" : "emerald";
  const days = daysUntil(event.desired_date);
  const doneCount = (event.tasks || []).filter(t => t.done).length;
  const totalCount = (event.tasks || []).length;
  const pct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  const toggleTask = async (taskId) => {
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/move-radar/events/${event.id}/tasks/${taskId}/toggle`,
        {}, auth());
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Toggle failed"));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm("Dismiss this move event?")) return;
    try {
      await axios.patch(`${BASE}/api/move-radar/events/${event.id}`,
        { status: "dismissed" }, auth());
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dismiss failed"));
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this move event permanently?")) return;
    try {
      await axios.delete(`${BASE}/api/move-radar/events/${event.id}`, auth());
      toast.success("Deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed"));
    }
  };

  // Flag the unit vacant in Vacant Security — one tap takes the move-out
  // address + desired date and seeds a vacancy row so the squatter-risk
  // hotlist starts tracking it immediately.
  const flagVacant = async () => {
    if (!window.confirm(
      `Flag "${event.address}${event.unit ? ' · Unit ' + event.unit : ''}" as vacant_unsecured in Vacant Security?\n\n` +
      `Day vacant will default to ${event.desired_date || "today"}. You can edit the row afterwards.`
    )) return;
    try {
      const r = await axios.post(`${BASE}/api/vacant-security/add-vacant`, {
        address: event.address,
        unit: event.unit || "",
        vacancy_status: "vacant_unsecured",
        went_vacant_at: event.desired_date
          ? new Date(event.desired_date + "T12:00:00Z").toISOString()
          : "",
        notes: `Auto-created from Move-Out event · tenant ${event.tenant_name || "—"}`,
      }, auth());
      toast.success(
        r.data?.created
          ? `✓ Flagged vacant · ${r.data.address}`
          : `✓ Updated vacancy · ${r.data.address}`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Flag failed"));
    }
  };

  const bg = brand === "rose"    ? "bg-rose-50 border-rose-300 text-rose-900"
                                 : "bg-emerald-50 border-emerald-300 text-emerald-900";

  return (
    <div className={`border-2 ${event.status === "open" ? bg.split(" ")[1] : "border-slate-300"} bg-white`}
      data-testid={`event-card-${event.id}`}>
      <div className="flex items-start gap-2 p-3 border-b-2 border-slate-100">
        <div className={`inline-flex items-center gap-1 border-2 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest ${bg}`}>
          {isOut ? <ArrowRight size={12} weight="bold"/> : <ArrowLeft size={12} weight="bold"/>}
          {isOut ? "Move-Out" : "Move-In"}
        </div>
        <div className="min-w-0">
          <div className="font-black text-sm truncate">{event.tenant_name || "(unknown tenant)"}</div>
          <div className="text-xs text-slate-600 truncate">
            <MapPin size={11} weight="fill" className="inline mr-0.5 text-slate-400"/>
            {event.address || "—"}{event.unit ? ` · Unit ${event.unit}` : ""}
          </div>
        </div>
        <div className="ml-auto text-right">
          {event.desired_date && (
            <div className="text-xs font-black">
              <Calendar size={11} weight="fill" className="inline mr-0.5 text-slate-400"/>
              {event.desired_date}
            </div>
          )}
          {days !== null && (
            <div className={`text-[10px] font-black uppercase tracking-widest ${
              days < 0 ? "text-slate-400" : days <= 7 ? "text-red-600" : days <= 21 ? "text-amber-600" : "text-emerald-700"
            }`}>
              {days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? "TODAY" : `${days}d out`}
            </div>
          )}
        </div>
        <button onClick={() => setExpanded(v => !v)}
          data-testid={`event-expand-${event.id}`}
          className="h-7 w-7 border-2 border-slate-300 hover:bg-slate-100 inline-flex items-center justify-center">
          {expanded ? <CaretDown size={12}/> : <CaretRight size={12}/>}
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-100 overflow-hidden">
            <div className={`h-full ${isOut ? "bg-rose-500" : "bg-emerald-500"}`}
              style={{ width: `${pct}%` }}/>
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 w-16 text-right">
            {doneCount} / {totalCount} · {pct}%
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-3 border-t-2 border-slate-100 space-y-3">
          {(event.reason || event.deposit_return_via || event.owner) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              {event.reason && <Field label="Reason" value={event.reason}/>}
              {event.deposit_return_via && <Field label="Deposit Via" value={event.deposit_return_via}/>}
              {event.owner && <Field label="Owner" value={event.owner}/>}
            </div>
          )}

          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">
              Punch List
            </div>
            <div className="space-y-1">
              {(event.tasks || []).map(t => (
                <label key={t.id}
                  data-testid={`task-${t.id}`}
                  className="flex items-start gap-2 p-2 border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={!!t.done} disabled={busy}
                    onChange={() => toggleTask(t.id)}
                    className="mt-0.5 w-4 h-4 accent-slate-900"/>
                  <span className={`text-sm ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>
                    {t.text}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t-2 border-dashed border-slate-200">
            <div className="text-[10px] text-slate-500 font-medium">
              Source: {event.source} · {event.source_channel} · Created {(event.created_at || "").slice(0, 10)}
            </div>
            <button onClick={dismiss}
              data-testid={`event-dismiss-${event.id}`}
              className="ml-auto h-8 px-2 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest">
              Dismiss
            </button>
            {isOut && event.address && (
              <button onClick={flagVacant}
                data-testid={`event-flag-vacant-${event.id}`}
                title="Create a vacant_unsecured row in Vacant Security with this address + move-out date"
                className="h-8 px-2 border-2 border-rose-900 bg-rose-600 hover:bg-rose-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
                🚨 Flag Vacant
              </button>
            )}
            <button onClick={del}
              data-testid={`event-delete-${event.id}`}
              className="h-8 px-2 border-2 border-red-300 hover:bg-red-50 text-red-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <Trash size={12}/> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function Field({ label, value }) {
  return (
    <div className="border border-slate-200 bg-slate-50 px-2 py-1">
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-medium text-slate-800 break-words">{value}</div>
    </div>
  );
}
